import { useState, useCallback, useEffect, useRef } from "react";
import StaffHeader from "@/components/StaffHeader";
import api from "@/lib/api";
import { AlertTriangle } from "lucide-react";
import DiwaliFilters from "@/components/admin/diwali/DiwaliFilters";
import DiwaliSummaryCards from "@/components/admin/diwali/DiwaliSummaryCards";
import DiwaliChannelChart from "@/components/admin/diwali/DiwaliChannelChart";
import DiwaliCampaignContentTable from "@/components/admin/diwali/DiwaliCampaignContentTable";
import DiwaliDailyChart from "@/components/admin/diwali/DiwaliDailyChart";
import DiwaliDonorsTable from "@/components/admin/diwali/DiwaliDonorsTable";
import MetaSpendCard from "@/components/admin/MetaSpendCard";

/** 'Today' in IST (Asia/Kolkata), not the browser's local/UTC date - the
 * campaign and its staff are India-based, so the default date range must
 * match the IST calendar day the backend uses for filtering/bucketing. */
function todayIST() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(new Date());
}

function defaultRange() {
  return { start: "2026-09-30", end: todayIST() };
}

/** Read-only Diwali campaign donor report. Stays available year-round (it
 * just filters guardian_donations/guardian_subscriptions by
 * campaign=diwali2026 - independent of the campaign's own active/inactive
 * window) - see routers/diwali_admin.py. No auto-polling: data loads once on
 * mount and again only via the Refresh button or a filter change. */
export default function DiwaliAdmin() {
  const [range, setRange] = useState(defaultRange());
  const [refreshKey, setRefreshKey] = useState(0);
  const [summary, setSummary] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);
  const requestIdRef = useRef(0);

  const fetchSummary = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setRefreshing(true);
    setError(false);
    try {
      const { data } = await api.get("/admin/diwali/summary", { params: { start_date: range.start, end_date: range.end } });
      if (requestId !== requestIdRef.current) return;
      setSummary(data);
    } catch {
      if (requestId !== requestIdRef.current) return;
      setError(true);
    } finally {
      if (requestId === requestIdRef.current) setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.start, range.end, refreshKey]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return (
    <div className="min-h-screen bg-amtm-bg" data-testid="diwali-admin-page">
      <StaffHeader />
      <div className="max-w-6xl mx-auto px-4 py-6 pb-24">
        <h1 className="font-display text-3xl text-amtm-text mb-6" data-testid="diwali-admin-title">Diwali Campaign</h1>
        <DiwaliFilters range={range} onChange={setRange} onRefresh={() => setRefreshKey((k) => k + 1)} refreshing={refreshing} />
        <MetaSpendCard testId="diwali-meta-spend-card" />
        {error ? (
          <div className="flex flex-col items-center gap-2 py-12 text-amtm-textMuted" data-testid="diwali-summary-error">
            <AlertTriangle className="h-6 w-6 text-destructive" />
            <p>Couldn't load the campaign summary. Try Refresh above.</p>
          </div>
        ) : (
          <>
            <DiwaliSummaryCards data={summary} />
            <DiwaliChannelChart data={summary} />
            <DiwaliDailyChart data={summary} />
            <DiwaliCampaignContentTable data={summary} />
          </>
        )}
        <DiwaliDonorsTable range={range} refreshKey={refreshKey} />
      </div>
    </div>
  );
}
